import React, { useState } from 'react';
import { bool, func, object, shape, string } from 'prop-types';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../util/reactIntl';
import { required, emailFormatValid, ytunnusValid, composeValidators } from '../../util/validators';
import { propTypes } from '../../util/types';
import { CLEVENIO_COUNTRIES } from '../../util/invoicing';
import getCountryCodes from '../../translations/countryCodes';
import {
  Form,
  FieldTextInput,
  InlineTextButton,
  PrimaryButton,
  SecondaryButton,
} from '../../components';

import css from './RequestInvoicingForm.css';

const clean = v => (typeof v === 'string' && v.trim() ? v.trim() : null);

// The POST /api/request-invoicing body (invoice path spec §3). Company,
// address and country come from the billing profile; a Finnish lookup hit
// supplies the legal name; the customer types only the delivery details.
export const requestPayloadFor = (billing, values, lookupCompany) => {
  const usesLookup = CLEVENIO_COUNTRIES.includes(billing.country);
  return {
    country: billing.country,
    ...(usesLookup ? { businessId: values.businessId.trim() } : {}),
    billing: {
      legalName: (lookupCompany && lookupCompany.name) || billing.legalName,
      registrationNumber: usesLookup ? null : clean(values.registrationNumber),
      vatNumber: clean(values.vatNumber) || billing.vatNumber || null,
      street: billing.street,
      postalCode: billing.postalCode,
      city: billing.city,
      country: billing.country,
      email: values.email,
      eInvoiceAddress: clean(values.eInvoiceAddress),
      eInvoiceOperator: clean(values.eInvoiceOperator),
      contactPerson: values.contactPerson,
    },
  };
};

const countryName = (intl, code) => {
  const match = getCountryCodes(intl.locale).find(c => c.code === code);
  return match ? match.name : code;
};

const RequestInvoicingFormComponent = props => {
  const {
    rootClassName,
    className,
    formId,
    intl,
    billing,
    currentUser,
    onLookupCompany,
    lookupInProgress,
    lookupResult,
    lookupError,
    onSubmit,
    submitInProgress,
    submitError,
    onEditBilling,
    onCancel,
  } = props;

  const [lookedUpId, setLookedUpId] = useState(null);
  const usesLookup = CLEVENIO_COUNTRIES.includes(billing.country);
  const profile = (currentUser && currentUser.attributes && currentUser.attributes.profile) || {};
  const id = name => `${formId}.${name}`;

  const initialValues = {
    businessId: '',
    registrationNumber: billing.businessProofRef || '',
    vatNumber: usesLookup ? '' : billing.vatNumber || '',
    email: (currentUser && currentUser.attributes && currentUser.attributes.email) || '',
    eInvoiceAddress: '',
    eInvoiceOperator: '',
    contactPerson:
      [profile.firstName, profile.lastName].filter(Boolean).join(' ') || profile.displayName || '',
  };

  const requiredMsg = intl.formatMessage({ id: 'RequestInvoicingModal.required' });
  const emailMsg = intl.formatMessage({ id: 'RequestInvoicingModal.emailInvalid' });
  const ytunnusMsg = intl.formatMessage({ id: 'RequestInvoicingModal.businessIdInvalid' });

  const company = lookupResult && lookupResult.company;
  const lookupDone = lookedUpId !== null && !lookupInProgress;
  // The lookup result is only trusted while the business id field still
  // matches what was looked up — editing it after a lookup must not let a
  // stale company ride along with a different id (fix 3).
  const matchesLookedUpId = values =>
    lookedUpId !== null && values.businessId.trim() === lookedUpId;

  return (
    <FinalForm
      initialValues={initialValues}
      onSubmit={values =>
        onSubmit(
          requestPayloadFor(
            billing,
            values,
            lookupDone && matchesLookedUpId(values) ? company : null
          )
        ).catch(() => null)
      }
      render={({ handleSubmit, values, invalid }) => {
        const lookupMatches = matchesLookedUpId(values);
        const runLookup = () => {
          const trimmed = values.businessId.trim();
          setLookedUpId(trimmed);
          onLookupCompany({ country: billing.country, businessId: trimmed }).catch(() => null);
        };
        const address = [
          billing.street,
          [billing.postalCode, billing.city].filter(Boolean).join(' '),
          countryName(intl, billing.country),
        ]
          .filter(Boolean)
          .join(', ');

        return (
          <Form
            className={classNames(rootClassName || css.root, className)}
            onSubmit={handleSubmit}
          >
            <h3 className={css.title}>
              <FormattedMessage id="RequestInvoicingForm.companyTitle" />
            </h3>
            <div className={css.companyCard}>
              <div className={css.companyName}>{billing.legalName}</div>
              <div className={css.companyAddress}>{address}</div>
              <div className={css.companySource}>
                <span>
                  <FormattedMessage id="RequestInvoicingForm.fromBilling" />
                </span>
                <InlineTextButton type="button" onClick={onEditBilling}>
                  <FormattedMessage id="RequestInvoicingForm.editBilling" />
                </InlineTextButton>
              </div>
            </div>

            {usesLookup ? (
              <div className={css.lookupRow}>
                <FieldTextInput
                  id={id('businessId')}
                  name="businessId"
                  type="text"
                  label={intl.formatMessage({ id: 'RequestInvoicingModal.businessId' })}
                  validate={composeValidators(required(requiredMsg), ytunnusValid(ytunnusMsg))}
                />
                <SecondaryButton
                  type="button"
                  className={css.lookupButton}
                  inProgress={lookupInProgress}
                  disabled={lookupInProgress || !values.businessId}
                  onClick={runLookup}
                >
                  <FormattedMessage id="RequestInvoicingModal.lookup" />
                </SecondaryButton>
              </div>
            ) : (
              <div className={css.row}>
                <FieldTextInput
                  id={id('registrationNumber')}
                  name="registrationNumber"
                  type="text"
                  label={intl.formatMessage({ id: 'RequestInvoicingModal.registrationNumber' })}
                />
                <FieldTextInput
                  id={id('vatNumber')}
                  name="vatNumber"
                  type="text"
                  label={intl.formatMessage({ id: 'RequestInvoicingModal.vatNumber' })}
                />
              </div>
            )}

            {usesLookup && lookupDone && lookupMatches && lookupError ? (
              <p className={css.hint}>
                <FormattedMessage id="RequestInvoicingModal.lookupFailed" />
              </p>
            ) : null}
            {usesLookup &&
            lookupDone &&
            lookupMatches &&
            !lookupError &&
            lookupResult &&
            !company ? (
              <p className={css.hint}>
                <FormattedMessage id="RequestInvoicingModal.lookupNotFound" />
              </p>
            ) : null}
            {usesLookup && lookupDone && lookupMatches && company ? (
              <div className={css.confirmCard}>
                <div className={css.confirmQuestion}>
                  <FormattedMessage id="RequestInvoicingForm.confirmCompany" />
                </div>
                {company.name}
                {company.companyForm ? ` · ${company.companyForm}` : ''}
                <br />
                {[
                  company.address && company.address.street,
                  company.address && company.address.postalCode,
                  company.address && company.address.city,
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            ) : null}

            <h3 className={css.title}>
              <FormattedMessage id="RequestInvoicingForm.deliveryTitle" />
            </h3>
            <FieldTextInput
              id={id('email')}
              name="email"
              type="email"
              label={intl.formatMessage({ id: 'RequestInvoicingModal.email' })}
              validate={composeValidators(required(requiredMsg), emailFormatValid(emailMsg))}
            />
            <div className={css.row}>
              <FieldTextInput
                id={id('eInvoiceAddress')}
                name="eInvoiceAddress"
                type="text"
                label={intl.formatMessage({ id: 'RequestInvoicingModal.eInvoiceAddress' })}
              />
              <FieldTextInput
                id={id('eInvoiceOperator')}
                name="eInvoiceOperator"
                type="text"
                label={intl.formatMessage({ id: 'RequestInvoicingModal.eInvoiceOperator' })}
              />
            </div>
            <FieldTextInput
              id={id('contactPerson')}
              name="contactPerson"
              type="text"
              label={intl.formatMessage({ id: 'RequestInvoicingModal.contactPerson' })}
              validate={required(requiredMsg)}
            />

            {submitError ? (
              <p className={classNames(css.hint, css.error)}>
                <FormattedMessage id="RequestInvoicingModal.submitFailed" />
              </p>
            ) : null}

            <div className={css.actions}>
              <PrimaryButton
                type="submit"
                inProgress={submitInProgress}
                disabled={submitInProgress || invalid}
              >
                <FormattedMessage id="RequestInvoicingModal.submit" />
              </PrimaryButton>
              {onCancel ? (
                <InlineTextButton type="button" className={css.cancel} onClick={onCancel}>
                  <FormattedMessage id="RequestInvoicingForm.cancel" />
                </InlineTextButton>
              ) : null}
            </div>
          </Form>
        );
      }}
    />
  );
};

RequestInvoicingFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  currentUser: null,
  lookupInProgress: false,
  lookupResult: null,
  lookupError: null,
  submitInProgress: false,
  submitError: null,
  onCancel: null,
};

RequestInvoicingFormComponent.propTypes = {
  rootClassName: string,
  className: string,
  formId: string.isRequired,
  intl: intlShape.isRequired,
  billing: shape({ legalName: string, country: string.isRequired }).isRequired,
  currentUser: propTypes.currentUser,
  onLookupCompany: func.isRequired,
  lookupInProgress: bool,
  lookupResult: shape({ company: object, revenue: object }),
  lookupError: propTypes.error,
  onSubmit: func.isRequired,
  submitInProgress: bool,
  submitError: propTypes.error,
  onEditBilling: func.isRequired,
  onCancel: func,
};

const RequestInvoicingForm = injectIntl(RequestInvoicingFormComponent);
RequestInvoicingForm.displayName = 'RequestInvoicingForm';
export default RequestInvoicingForm;
